import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Paper,
  Typography,
  Box,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import { UserForm, UserFormData } from "../components/UserForm";
import { useFindUser, useEditUser } from "../../../../../api/bpm/endpoints";
import { useAuth } from "../../../../../auth";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/__auth/admin/users/$userId/edit")({
  component: EditUserPage,
  staticData: {
    title: "routes.editUser",
  },
});

function EditUserPage() {
  const { t } = useTranslation();
  const { userId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user: authUser } = useAuth();
  const tenant = authUser?.tenant || "default-domain";

  const { data: userDataRaw, isLoading } = useFindUser(tenant, userId);
  const userData = (userDataRaw as any)?.data || userDataRaw;

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const editMutation = useEditUser({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [`/v1/${tenant}/users`] });
        queryClient.invalidateQueries({
          queryKey: [`/v1/${tenant}/users/${userId}`],
        });
        setSnackbar({
          open: true,
          message: t("admin.users.updateSuccess"),
          severity: "success",
        });
        setTimeout(
          () => navigate({ to: "/admin/users", search: { page: 1, size: 10 } }),
          1500,
        );
      },
      onError: () => {
        setSnackbar({
          open: true,
          message: "Erro ao atualizar usuário.",
          severity: "error",
        });
      },
    },
  });

  const handleSubmit = async (data: UserFormData) => {
    const payload = {
      tenant,
      userId,
      data: {
        name: data.name,
        email: data.email,
        password: data.password || undefined,
        confirmationPassword: data.confirmationPassword || undefined,
        cpf: data.cpf || (null as unknown as string),
        matriculaInstitucional: data.matriculaInstitucional || undefined,
        dataNascimento: data.dataNascimento || undefined,
        stockingAreas: data.stockingAreas?.map((sa: any) => ({ id: sa.id })),
        job: data.job ? { id: data.job.id } : undefined,
        occupation: data.occupation ? { id: data.occupation.id } : undefined,
        accessProfiles: data.accessProfiles?.map((ap: any) => ({
          id: ap.id,
          isUnderteminedTime: true,
        })),
        groups: data.groups?.map((g: any) => ({
          id: g.id,
          isUnderteminedTime: true,
        })),
      },
    };

    editMutation.mutate({ userId, tenant, data: payload.data });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        {t("admin.users.editTitle")}{" "}
      </Typography>
      <Paper sx={{ p: 3, mt: 3 }}>
        <UserForm
          initialData={userData}
          onSubmit={handleSubmit}
          onCancel={() =>
            navigate({ to: "/admin/users", search: { page: 1, size: 10 } })
          }
          isLoading={editMutation.isPending}
        />
      </Paper>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
